// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
/* Starter Custom Code
import {
    HasProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
*/
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
// import Button, { ButtonPresentationProps } from "@insite/mobius/Button"; // Starter Custom Code
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
// import React, { useContext, useState } from "react"; // Starter Custom Code
import { connect, ResolveThunks } from "react-redux";

/* Starter Custom Code - Start */
import React, { useContext, useMemo, useState } from "react";
import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import PlusCircleFill from "@store/Library/Components/Toolkit/Icons/PlusCircle/PlusCircleFill";
// import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button"; // Store Custom Code
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { useCanAddToCart, useHasEnoughInventory } from "@toolkit/Framework/Store/Data/Products/ProductsHooks";
import { css } from "styled-components";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { IconWrapper } from "@store/MobiusOverrides/Icon/Icon";

/* Store Custom Code - Start */
import getColor from "@insite/mobius/utilities/getColor";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { loadingDotsStyles } from "@store/Themes/common/mixins/typography";
import constants from "@store/constants";
import useCanOnlyOrderSample from "@store/Framework/Common/Hooks/samples/useCanOnlyOrderSample";
import getSampleProductContext from "@store/Framework/Common/Utilities/samples/getSampleProductContext";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Store Custom Code - End */

export interface ProductAddToCartButtonProps
    extends Omit<OwnProps, keyof ButtonPresentationProps | keyof LinkPresentationProps> {}

export const useShowProductAddToCartButton = (
    configurationCompleted: boolean | undefined,
    variantSelectionCompleted: boolean | undefined,
    isSample?: boolean, // Store Custom Code
) => {
    const productContext = useContext(ProductContext);
    const { product, productInfo } = productContext;
    const hasEnoughInventory = useHasEnoughInventory(productContext);
    const canAddToCart = useCanAddToCart(product, productInfo, configurationCompleted, variantSelectionCompleted);

    /* Store Custom Code - Start */
    if (isSample) {
        return true;
    }
    /* Store Custom Code - End */

    return !((!hasEnoughInventory && !productInfo.failedToLoadInventory) || !canAddToCart);
};
/* Starter Custom Code - End */

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ProductAddToCartButtonStyles; // Starter Custom Code
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    // Starter Custom Code
    overridedStyles?: ProductAddToCartButtonStyles;
    isLink?: boolean;
    showButtonIconOnly?: boolean;
    // Store Custom Code
    isSample?: boolean;
    isDisabled?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        isUpdatingCart, // Starter Custom Code
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

/* Starter Custom Code - Start */
export interface ProductAddToCartButtonStyles {
    button?: ButtonPresentationProps;
    link?: LinkPresentationProps;
    // Store Custom Code
    buttonLabelWrapper?: TypographyPresentationProps;
    labelText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
}

export const productAddToCartButtonStyles: ProductAddToCartButtonStyles = {
    button: {
        mergeCss: true, // Store Custom Code
        variant: "primary",
        /* Store Removed - css */
    },
    link: linkGetActionLinkProps(PlusCircleFill),
    /* Store Custom Code - Start */
    buttonLabelWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
        `,
    },
    labelText: {
        css: css`
            display: block;
            line-height: ${x => x.theme.design.typography.lineHeight.base};
            letter-spacing: ${x => x.theme.design.typography.letterSpacing.large};
        `,
    },
    realTimeText: {
        css: css`
            ${loadingDotsStyles()}
            min-width: 1em;
        `,
    },
    /* Store Custom Code - End */
};
/* Starter Custom Code - End */

const ProductAddToCartButton = ({
    productContext: baseProductContext, // Store Custom Code
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    overridedStyles, // Starter Custom Code
    isLink, // Starter Custom Code
    isUpdatingCart, // Starter Custom Code
    showButtonIconOnly, // Starter Custom Code
    isSample, // Store Custom Code
    isDisabled, // Store Custom Code
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);

    /* Starter Custom Code - Start */
    const trackAddToCart = ga.use.trackAddToCart();
    const styles = useExpandProps(productAddToCartButtonStyles, extendedStyles, overridedStyles);
    const [isLoading, setIsLoading] = useState(false);

    const buttonStyles = useMemo(() => {
        if (showButtonIconOnly) {
            return mergeProps<ProductAddToCartButtonStyles["button"]>(styles.button, {
                css: css`
                    ${IconWrapper} {
                        margin: initial;
                    }
                `,
            });
        }
        return styles.button;
    }, [showButtonIconOnly, styles.button]);
    /* Starter Custom Code - End */

    /* Store Custom Code - Start */
    const { product, productInfo } = React.useMemo(() => {
        return isSample ? getSampleProductContext(baseProductContext) : baseProductContext;
    }, [baseProductContext]);

    const canOnlyOrderSample = useCanOnlyOrderSample(product);
    const showProductAddToCartButton = useShowProductAddToCartButton(
        configurationCompleted,
        variantSelectionCompleted,
        isSample,
    );

    if (!showProductAddToCartButton || (canOnlyOrderSample && !isSample)) {
        return null;
    }

    const { id: productId, allowZeroPricing, quoteRequired } = product;
    const { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory, inventory, failedToLoadPricing } = productInfo;
    /* Store Custom Code - End */

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();
        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        setIsLoading(true); // Starter Custom Code

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure: isSample ? constants.sampleUOM : unitOfMeasure, // Store Custom Code
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        setIsLoading(false); // Starter Custom Code
        trackAddToCart(); // Starter Custom Code

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
            });
        }
    };

    /* Store Custom Code - Start */
    const baseLabel = labelOverride ?? translate("Add to Cart");
    let buttonLabel: React.ReactNode = baseLabel;

    if (isSample && !failedToLoadInventory && !failedToLoadPricing) {
        if (!inventory) {
            buttonLabel = <Typography {...styles.realTimeText} />;
        } else if (!pricing) {
            buttonLabel = (
                <Typography {...styles.buttonLabelWrapper}>
                    <Typography {...styles.labelText}>{translate(`{0}:`, baseLabel)}&nbsp;</Typography>
                    <Typography {...styles.labelText}>
                        <Typography {...styles.realTimeText} />
                        {translate("{0}/EA", "")}
                    </Typography>
                </Typography>
            );
        } else {
            buttonLabel = (
                <Typography {...styles.buttonLabelWrapper}>
                    <Typography {...styles.labelText}>{translate(`{0}:`, baseLabel)}&nbsp;</Typography>
                    <Typography {...styles.labelText}>
                        {translate("{0}/EA", pricing.unitRegularPriceDisplay)}
                    </Typography>
                </Typography>
            );
        }
    }
    /* Store Custom Code - End */

    return (
        <>
            {/* Starter Custom Code - Start */}
            {isLink ? (
                <Link
                    {...styles.link}
                    onClick={addToCartClickHandler}
                    disabled={qtyOrdered <= 0 || isUpdatingCart || isDisabled} // Store Custom Code
                    {...otherProps}
                >
                    {baseLabel /* Store Custom Code */}
                </Link>
            ) : (
                <Button
                    {...buttonStyles}
                    onClick={addToCartClickHandler}
                    disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled || isDisabled} // Store Custom Code
                    isLoading={isLoading}
                    forceWrap={true} // Store Custom Code
                    {...otherProps}
                >
                    {!showButtonIconOnly && <span>{buttonLabel /* Store Custom Code */}</span>}
                    {showButtonIconOnly ? <VisuallyHidden>{buttonLabel /* Store Custom Code */}</VisuallyHidden> : null}
                </Button>
            )}
            {/* Starter Custom Code - End */}
        </>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
